import type { BlogPost } from '../@types/BlogPost';
import type { Company, ReferentMutation } from '../@types/Company';
import { getValidReferents } from './branding';
import { getCompanyDisplayName } from './company';
import { JWTHeader } from './JWTHeader';
import { buildUnsubscribeUrl } from './newsletterMail';
import { DJ_URL } from './routes';

// Simple interface for structured content (legacy support)
interface NewsletterStructuredContent {
  [key: string]: Record<string, string | number | boolean | null | undefined>;
}

const ARTICLE_SIZE_LIMIT = 500;

/**
 * Formats a plain 10-digit French phone number with space separators.
 * e.g. "0615163427" → "06 15 16 34 27"
 * Already-formatted or non-10-digit strings are returned as-is.
 */
export const formatPhoneNumber = (phone: string): string => {
  const digits = phone.replaceAll(/\s/g, '');
  if (/^\d{10}$/.test(digits)) {
    return digits.replaceAll(/(\d{2})(?=\d)/g, '$1 ');
  }
  return phone;
};

export interface PreviewColors {
  color1: string;
  color2: string;
  color3: string;
}

// Utility constant for attribute mapping
const ATTRIBUTE_TYPE_MAP: Record<string, string> = {
  monthly: 'monthly',
  bimonthly: 'bimonthly',
  quarterly: 'quartely', // Note the spelling difference
  'quality-reference-reminder': 'quality-reference',
  'mandatory-first': 'mandatory-first',
  fixed: 'fixed',
};

export const isLightColor = (hexColor?: string) => {
  if (!hexColor) { return true; }
  const color = hexColor.replace('#', '');
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 128;
};

export const applyPreviewColors = (content: string, brandingColors: PreviewColors) => {
  if (!content) { return ''; }
  return content
    .replaceAll('[data-blog-color1]', brandingColors.color1)
    .replaceAll('[data-blog-color2]', brandingColors.color2)
    .replaceAll('[data-blog-color3]', brandingColors.color3);
};

/**
 * Generate a QR code URL for the company blog or referent's external newsletter
 * @param companySiren - Company SIREN number
 * @param externalNewsletterUrl - Optional external newsletter URL (overrides blog URL)
 */
export const generateQRCodeUrl = (companySiren: string, externalNewsletterUrl?: string): string => {
  let targetUrl: string;
  
  if (externalNewsletterUrl) {
    // Use the referent's external newsletter URL
    targetUrl = externalNewsletterUrl;
  } else {
    // Default to company blog URL
    const domain = window.location.origin;
    targetUrl = `${domain}/blog/company/${companySiren}`;
  }
  
  const encodedUrl = encodeURIComponent(targetUrl);
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodedUrl}`;
};

export const createContentPreview = (content: string): string => {
  if (!content) {
    return '';
  }
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;
  
  if (tempDiv.innerHTML.length <= ARTICLE_SIZE_LIMIT) {
    return tempDiv.innerHTML;
  }
  
  let charCount = 0;
  let result = '';
  const openTags: string[] = [];
  const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'param', 'source', 'track', 'wbr'];
  
  const processNode = (node: Node): boolean => {
    if (charCount >= ARTICLE_SIZE_LIMIT) {
      return false;
    }
    
    switch (node.nodeType) {
    case Node.TEXT_NODE: {
      const text = node.textContent || '';
      const remainingChars = ARTICLE_SIZE_LIMIT - charCount;
      if (text.length <= remainingChars) {
        result += text;
        charCount += text.length;
        return true;
      } 
      result += `${text.substring(0, remainingChars)}...`;
      charCount = ARTICLE_SIZE_LIMIT;
      return false;
    }
    case Node.ELEMENT_NODE: {
      const elem = node as Element;
      const tagName = elem.nodeName.toLowerCase();
      if (tagName === 'script' || tagName === 'style') {
        return true;
      }

      const isSelfClosing = selfClosingTags.includes(tagName);
      let startTag = `<${tagName}`;
      for (let i = 0; i < elem.attributes.length; i++) {
        const attr = elem.attributes[i];
        startTag += ` ${attr.name}="${attr.value}"`;
      }
      if (isSelfClosing) {
        startTag += ' />';
        result += startTag;
        return true;
      }
      startTag += '>';
      result += startTag;
      openTags.push(tagName);

      let allChildrenProcessed = true;
      for (let i = 0; i < node.childNodes.length; i++) {
        if (!processNode(node.childNodes[i])) {
          allChildrenProcessed = false;
          break;
        }
      }
      if (allChildrenProcessed) {
        result += `</${tagName}>`;
        openTags.pop();
      } else {
        return false;
      }
      return allChildrenProcessed;
    }
    default:
      return true;
    }
  };
  
  for (let i = 0; i < tempDiv.childNodes.length; i++) {
    if (!processNode(tempDiv.childNodes[i])) {
      break;
    }
  }
  
  for (let i = openTags.length - 1; i >= 0; i--) {
    result += `</${openTags[i]}>`;
  }
  
  return result;
};

/**
 * Create HTML content for a blog post
 */
function createPostContent(post: BlogPost, template: string, companySiren: string, isFirstPage: boolean = false, newsletterType: string = 'monthly'): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = template;
  
  const blogImage = tempDiv.querySelector('[data-blog-image]');
  if (blogImage && post.mainImageName) {
    blogImage.setAttribute('src', `/uploads/blog-posts/${post.mainImageName}`);
    blogImage.setAttribute('alt', post.title);
  } else if (blogImage) {
    blogImage.parentNode?.removeChild(blogImage);
  }
  
  const descriptionElement = tempDiv.querySelector('p');
  let lengthLimit = ARTICLE_SIZE_LIMIT;
  const attributeType = ATTRIBUTE_TYPE_MAP[newsletterType] || newsletterType;

  if (descriptionElement) {
    if (isFirstPage) {
      const firstPageTypeAttr = `data-ai-first-page-length-${attributeType}`;
      if (descriptionElement.hasAttribute(firstPageTypeAttr)) {
        const typeSpecificLength = parseInt(descriptionElement.getAttribute(firstPageTypeAttr) || '0', 10);
        if (typeSpecificLength > 0) {
          lengthLimit = typeSpecificLength;
        }
      } else if (descriptionElement.hasAttribute('data-ai-first-page-length')) {
        const firstPageLength = parseInt(descriptionElement.getAttribute('data-ai-first-page-length') || '0', 10);
        if (firstPageLength > 0) {
          lengthLimit = firstPageLength;
        }
      }
    } else {
      const standardTypeAttr = `data-ai-standard-length-${attributeType}`;
      if (descriptionElement.hasAttribute(standardTypeAttr)) {
        const typeSpecificLength = parseInt(descriptionElement.getAttribute(standardTypeAttr) || '0', 10);
        if (typeSpecificLength > 0) {
          lengthLimit = typeSpecificLength;
        }
      } else if (descriptionElement.hasAttribute('data-ai-standard-length')) {
        const standardLength = parseInt(descriptionElement.getAttribute('data-ai-standard-length') || '0', 10);
        if (standardLength > 0) {
          lengthLimit = standardLength;
        }
      }
    }
  }

  // remove description element
  // descriptionElement?.remove();
  
  const originalSizeLimit = ARTICLE_SIZE_LIMIT;
  (ARTICLE_SIZE_LIMIT as number) = lengthLimit;
  const truncatedContent = createContentPreview(post.content);
  (ARTICLE_SIZE_LIMIT as number) = originalSizeLimit;
  
  const domain = window.location.origin;
  const postUrl = `${domain}/blog/company/${companySiren}/${post.slug}`;
  
  return tempDiv.innerHTML
    .replaceAll('{post_title}', post.title)
    .replaceAll('{post_description}', truncatedContent)
    .replaceAll('{post_full_content}', post.content)
    .replaceAll('{post_url}', postUrl);
}

/**
 * Process blog groups template
 */
function processBlogGroups(
  blogGroups: NodeListOf<Element>,
  posts: BlogPost[],
  companySiren: string,
  initialBlogTemplateGroup?: string,
  newsletterType: string = 'monthly',
): string {
  const firstGroup = blogGroups[0];
  blogGroups.forEach((group, index) => {
    if (index > 0) {
      group.remove();
    }
  });
  
  if (initialBlogTemplateGroup) {
    const templateDoc = new DOMParser().parseFromString(initialBlogTemplateGroup, 'text/html');
    const templateGroup = templateDoc.querySelector('[data-blog-posts-group]');
    if (templateGroup) {
      firstGroup.innerHTML = templateGroup.innerHTML;
    }
  }
  
  const postsInGroup = firstGroup.querySelectorAll('[data-blog-posts]');
  const postsPerGroup = postsInGroup.length;
  let groupsNeeded = Math.ceil(posts.length / postsPerGroup);
  if (posts.length === 3 && postsPerGroup === 2) {
    groupsNeeded = 2;
  }
  
  const groupTemplate = firstGroup.cloneNode(true);
  const parentContainer = firstGroup.parentElement;
  if (!parentContainer) { return ''; }
  parentContainer.innerHTML = '';
  
  for (let i = 0; i < groupsNeeded; i++) {
    const newGroup = groupTemplate.cloneNode(true) as Element;
    const groupPosts = newGroup.querySelectorAll('[data-blog-posts]');
    groupPosts.forEach((postPlaceholder, index) => {
      const postIndex = (i * postsPerGroup) + index;
      const post = posts[postIndex];
      if (post) {
        postPlaceholder.innerHTML = createPostContent(post, postPlaceholder.innerHTML, companySiren, i === 0, newsletterType);
      } else {
        postPlaceholder.remove();
      }
    });
    parentContainer.appendChild(newGroup);
  }
  
  return parentContainer.ownerDocument.body.innerHTML;
}

/**
 * Process single blog section template
 */
function processSingleBlogSection(
  doc: Document,
  posts: BlogPost[],
  companySiren: string = '',
  initialBlogTemplate?: string,
  newsletterType: string = 'monthly',
): string {
  const existingBlogSection = doc.querySelector('[data-blog-posts]');
  if (!existingBlogSection || !initialBlogTemplate) {
    return doc.body.innerHTML;
  }
  
  const templateDoc = new DOMParser().parseFromString(initialBlogTemplate, 'text/html');
  const newBlogSection = templateDoc.querySelector('[data-blog-posts]');
  if (!newBlogSection) {
    return doc.body.innerHTML;
  }
  
  const templateItem = newBlogSection.querySelector('.blog-item');
  if (!templateItem) {
    return doc.body.innerHTML;
  }
  
  newBlogSection.innerHTML = posts.map(post => createPostContent(post, templateItem.outerHTML, companySiren, true, newsletterType)).join('');
  existingBlogSection.replaceWith(newBlogSection);
  
  return doc.body.innerHTML;
}

export const processSelectedBlogPosts = async(
  companySiren: string,
  posts: BlogPost[],
  templateContent: string,
  initialContent: string,
  blogTemplate: string,
  blogTemplateGroup: string,
  newsletterType: string = 'monthly',
): Promise<string> => {
  if (!posts.length) {
    return initialContent;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(initialContent, 'text/html');
  const blogGroups = doc.querySelectorAll('[data-blog-posts-group]');
  if (blogGroups.length > 0) {
    return processBlogGroups(blogGroups, posts, companySiren, blogTemplateGroup, newsletterType);
  }
  return processSingleBlogSection(doc, posts, companySiren, blogTemplate, newsletterType);
};

export const processPreviewContent = (templateContent: string, content: NewsletterStructuredContent) => {
  const processedContent = templateContent;
  const parser = new DOMParser();
  const doc = parser.parseFromString(processedContent, 'text/html');
  
  Object.entries(content).forEach(([section, sectionContent]) => {
    Object.entries(sectionContent).forEach(([field, value]) => {
      const selector = `[data-ai="${section}.${field}"]`;
      const element = doc.querySelector(selector);
      if (element) {
        const formattedValue = String(value ?? '').replaceAll('\n', '<br/>');
        element.innerHTML = formattedValue;
      }
    });
  });

  return doc.body.innerHTML;
};

function applyArticleLengthLimits(element: Element, isFirstPage: boolean, newsletterType: string = 'monthly'): void {
  const paragraphs = element.querySelectorAll('p');
  const attributeType = ATTRIBUTE_TYPE_MAP[newsletterType] || newsletterType;
  paragraphs.forEach(paragraph => {
    let lengthLimit = ARTICLE_SIZE_LIMIT;
    if (isFirstPage) {
      const firstPageTypeAttr = `data-ai-first-page-length-${attributeType}`;
      if (paragraph.hasAttribute(firstPageTypeAttr)) {
        const typeSpecificLength = parseInt(paragraph.getAttribute(firstPageTypeAttr) || '0', 10);
        if (typeSpecificLength > 0) {
          lengthLimit = typeSpecificLength;
        }
      } else if (paragraph.hasAttribute('data-ai-first-page-length')) {
        const firstPageLength = parseInt(paragraph.getAttribute('data-ai-first-page-length') || '0', 10);
        if (firstPageLength > 0) {
          lengthLimit = firstPageLength;
        }
      }
    } else {
      const standardTypeAttr = `data-ai-standard-length-${attributeType}`;
      if (paragraph.hasAttribute(standardTypeAttr)) {
        const typeSpecificLength = parseInt(paragraph.getAttribute(standardTypeAttr) || '0', 10);
        if (typeSpecificLength > 0) {
          lengthLimit = typeSpecificLength;
        }
      } else if (paragraph.hasAttribute('data-ai-standard-length')) {
        const standardLength = parseInt(paragraph.getAttribute('data-ai-standard-length') || '0', 10);
        if (standardLength > 0) {
          lengthLimit = standardLength;
        }
      }
    }
    const originalText = paragraph.textContent || '';
    if (originalText.length > lengthLimit) {
      paragraph.textContent = `${originalText.substring(0, lengthLimit)}...`;
    }
  });
}

function copyStyles(source: HTMLElement, target: HTMLElement) {
  const sourceStyles = source.style;
  for (let i = 0; i < sourceStyles.length; i++) {
    const property = sourceStyles[i];
    target.style.setProperty(property, sourceStyles.getPropertyValue(property), sourceStyles.getPropertyPriority(property));
  }
  target.className = `newsletter-page-content ${source.className}`.trim();
  [...source.attributes].forEach(attr => {
    if (attr.name !== 'style' && attr.name !== 'class' && attr.name !== 'data-ai') {
      target.setAttribute(attr.name, attr.value);
    }
  });
}

// Helper function to add remaining posts
function addRemainingPosts(
  originalPosts: BlogPost[],
  totalPosts: number,
  emptyBlogPostTemplate: HTMLElement,
  container: HTMLElement,
  companySiren: string,
  newsletterType: string,
): void {
  for (let i = totalPosts; i < originalPosts.length; i++) {
    const postContent = createPostContent(originalPosts[i], emptyBlogPostTemplate.outerHTML, companySiren, false, newsletterType);
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = postContent;
    const newPostElement = tempContainer.firstElementChild as HTMLElement;
    if (newPostElement) {
      container.appendChild(newPostElement);
    }
  }
}

export const formatForA4Pages = (content: string, originalPosts: BlogPost[] = [], newsletterType: string = 'monthly', companySiren: string = '', templateContent: string = ''): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  
  const mainContent = doc.querySelector('[data-ai="mainContent"]');
  if (!mainContent) {
    return content;
  }
  
  const header = doc.querySelector('[data-ai="header"]');
  const footer = doc.querySelector('[data-ai="footer"]');
  if (!header) {
    return content;
  }
  
  const introSection = mainContent.querySelector('[data-ai="mainContent.introSection"]');
  const blogPostsGroup = mainContent.querySelector('[data-blog-posts-group]');
  const blogPostsContainer = blogPostsGroup?.querySelector('div[style*="display: flex"]');
  
  if (blogPostsGroup && blogPostsContainer) {
    const pagesContainer = document.createElement('div');
    pagesContainer.className = 'newsletter-pages-container';
    
    // Save the original blog posts group structure before any modifications
    const originalBlogGroup = blogPostsGroup.cloneNode(true) as HTMLElement;
    
    // If we have the original template content, try to get a clean template from it
    let emptyBlogPostTemplate: HTMLElement | null = null;
    if (templateContent) {
      const templateDoc = parser.parseFromString(templateContent, 'text/html');
      const templateBlogPostsGroup = templateDoc.querySelector('[data-blog-posts-group]');
      if (templateBlogPostsGroup) {
        emptyBlogPostTemplate = templateBlogPostsGroup.querySelector('[data-blog-posts]')?.cloneNode(true) as HTMLElement;
      }
    }
    
    // Fallback to using the current content if we couldn't get a clean template
    if (!emptyBlogPostTemplate) {
      emptyBlogPostTemplate = blogPostsGroup.querySelector('[data-blog-posts]')?.cloneNode(true) as HTMLElement;
    }
    
    if (emptyBlogPostTemplate) {
      // Clear the content but keep the structure
      const titleElement = emptyBlogPostTemplate.querySelector('h3');
      if (titleElement) {
        titleElement.textContent = '{post_title}';
      }
      const descriptionElement = emptyBlogPostTemplate.querySelector('p');
      if (descriptionElement) {
        descriptionElement.textContent = '{post_description}';
      }
      const linkElement = emptyBlogPostTemplate.querySelector('a[href]');
      if (linkElement) {
        linkElement.setAttribute('href', '{post_url}');
      }
    }
    
    // Get all blog posts from the original content
    const allBlogPosts = blogPostsGroup.querySelectorAll('[data-blog-posts]');
    const totalPosts = allBlogPosts.length;
    const postsOnFirstPage = Math.min(2, totalPosts);
    const hasSecondPage = totalPosts > postsOnFirstPage || totalPosts >= 3 || originalPosts.length > postsOnFirstPage;
    
    // Create first page
    const firstPageContainer = document.createElement('div');
    firstPageContainer.className = 'newsletter-page';
    firstPageContainer.setAttribute('data-page', '1');
    
    const pageHeader = header.cloneNode(true) as HTMLElement;
    pageHeader.style.position = 'relative';
    firstPageContainer.appendChild(pageHeader);
    
    const firstPageMainContent = document.createElement('div');
    firstPageMainContent.setAttribute('data-ai', 'mainContent');
    firstPageMainContent.className = 'newsletter-page-content';
    if (mainContent instanceof HTMLElement) {
      copyStyles(mainContent, firstPageMainContent);
    }
    
    // Add intro section if it exists
    if (introSection) {
      firstPageMainContent.appendChild(introSection.cloneNode(true));
    }
    
    // Add non-blog-posts and non-intro content to the first page
    const mainContentChildren = [...mainContent.children];
    for (const child of mainContentChildren) {
      if (!child.contains(blogPostsGroup) && child !== introSection) {
        firstPageMainContent.appendChild(child.cloneNode(true));
      }
    }
    
    // Create a blog posts group for the first page
    const firstPageBlogGroup = originalBlogGroup.cloneNode(true) as HTMLElement;
    const firstPageBlogContainer = firstPageBlogGroup.querySelector('div[style*="display: flex"]') as HTMLElement;
    
    // Clear the container but keep its structure
    if (firstPageBlogContainer) {
      firstPageBlogContainer.innerHTML = '';
      
      // Add the first posts to the first page
      for (let i = 0; i < postsOnFirstPage; i++) {
        if (i < allBlogPosts.length) {
          const postClone = allBlogPosts[i].cloneNode(true) as HTMLElement;
          firstPageBlogContainer.appendChild(postClone);
        }
      }
    }
    
    firstPageMainContent.appendChild(firstPageBlogGroup);
    applyArticleLengthLimits(firstPageMainContent, true, newsletterType);
    firstPageContainer.appendChild(firstPageMainContent);
    pagesContainer.appendChild(firstPageContainer);
    
    // Create second page if needed
    if (hasSecondPage) {
      const secondPageContainer = document.createElement('div');
      secondPageContainer.className = 'newsletter-page newsletter-last-page';
      secondPageContainer.setAttribute('data-page', '2');
      
      const secondPageHeader = header.cloneNode(true) as HTMLElement;
      secondPageHeader.style.position = 'relative';
      secondPageContainer.appendChild(secondPageHeader);
      
      const secondPageMainContent = document.createElement('div');
      secondPageMainContent.setAttribute('data-ai', 'mainContent');
      secondPageMainContent.className = 'newsletter-page-content';
      if (mainContent instanceof HTMLElement) {
        copyStyles(mainContent, secondPageMainContent);
      }
      
      // Create a blog posts group for the second page using the original structure
      const secondPageBlogGroup = originalBlogGroup.cloneNode(true) as HTMLElement;
      const secondPageBlogContainer = secondPageBlogGroup.querySelector('div[style*="display: flex"]') as HTMLElement;
      
      if (secondPageBlogContainer) {
        // Clear the container but keep its structure
        secondPageBlogContainer.innerHTML = '';
        
        // Add the remaining posts from the DOM
        for (let i = postsOnFirstPage; i < totalPosts; i++) {
          const postClone = allBlogPosts[i].cloneNode(true) as HTMLElement;
          secondPageBlogContainer.appendChild(postClone);
        }
        
        // Add any posts from the original array that aren't in the DOM
        if (originalPosts.length > totalPosts && emptyBlogPostTemplate) {
          addRemainingPosts(originalPosts, totalPosts, emptyBlogPostTemplate, secondPageBlogContainer, companySiren, newsletterType);
        }
      }
      
      secondPageMainContent.appendChild(secondPageBlogGroup);
      applyArticleLengthLimits(secondPageMainContent, false, newsletterType);
      secondPageContainer.appendChild(secondPageMainContent);
      
      const pageFooter = footer ? footer.cloneNode(true) as HTMLElement : document.createElement('div');
      pageFooter.style.position = 'relative';
      secondPageContainer.appendChild(pageFooter);
      pagesContainer.appendChild(secondPageContainer);
    } else {
      // If there's no second page, add footer to the first page
      const pageFooter = footer ? footer.cloneNode(true) as HTMLElement : document.createElement('div');
      pageFooter.style.position = 'relative';
      firstPageContainer.appendChild(pageFooter);
      firstPageContainer.classList.add('newsletter-last-page');
    }
    
    // Add CSS for proper page formatting
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .newsletter-pages-container {
        display: flex;
        flex-direction: column;
        width: 210mm;
        margin: 0 auto;
        background-color: #f5f7fa;
      }
      
      .newsletter-page {
        width: 210mm;
        height: 297mm;
        page-break-after: always;
        position: relative;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        background-color: #f5f7fa;
      }
      
      .newsletter-last-page {
        height: 297mm !important; /* Reduce height by 0mm to prevent empty second page */
        page-break-after: avoid !important; /* Prevent page break after the last page */
      }
      
      .newsletter-page-content {
        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: 0%;
        overflow: hidden;
      }
      
      [data-blog-posts-group] div[style*="display: flex"] {
        display: flex !important;
        flex-wrap: wrap !important;
      }
      
      @media print {
        .newsletter-page {
          margin: 0;
          box-shadow: none;
          page-break-after: always;
        }
        .newsletter-last-page {
          height: 297mm !important; /* Reduce height by 0mm to prevent empty second page */
          page-break-after: avoid !important; /* Prevent page break after the last page */
        }
        [data-ai="header"] {
          position: running(header);
        }
        [data-ai="footer"] {
          position: running(footer);
        }
        @page {
          size: A4 portrait;
          margin: 0;
          @top-center {
            content: element(header);
          }
          @bottom-center {
            content: element(footer);
          }
        }
      }
    `;
    pagesContainer.appendChild(styleElement);
    return pagesContainer.outerHTML;
  }
  
  // Rest of the function for handling other layouts
  let articles = mainContent.querySelectorAll('[data-blog-posts]');
  if (articles.length === 0) {
    articles = mainContent.querySelectorAll('article');
    if (articles.length === 0) {
      articles = mainContent.querySelectorAll('div.article, div.blog-item, .blog-post');
      if (articles.length === 0) {
        articles = [...mainContent.children].filter(child => child !== introSection) as unknown as NodeListOf<Element>;
      }
    }
  }
  const articlesArray = [...articles];
  if (articlesArray.length <= 1) {
    const pagesContainer = document.createElement('div');
    pagesContainer.className = 'newsletter-pages-container';
    const pageContainer = document.createElement('div');
    pageContainer.className = 'newsletter-page newsletter-last-page';
    pageContainer.setAttribute('data-page', '1');
    const pageHeader = header.cloneNode(true) as HTMLElement;
    pageHeader.style.position = 'relative';
    pageContainer.appendChild(pageHeader);
    const pageMainContent = mainContent.cloneNode(true) as HTMLElement;
    pageMainContent.className = 'newsletter-page-content';
    if (mainContent instanceof HTMLElement) {
      pageMainContent.className = `newsletter-page-content ${mainContent.className}`.trim();
    }
    applyArticleLengthLimits(pageMainContent, true, newsletterType);
    pageContainer.appendChild(pageMainContent);
    const pageFooter = footer ? footer.cloneNode(true) as HTMLElement : document.createElement('div');
    pageFooter.style.position = 'relative';
    pageContainer.appendChild(pageFooter);
    pagesContainer.appendChild(pageContainer);
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .newsletter-pages-container {
        display: flex;
        flex-direction: column;
        width: 210mm;
        margin: 0 auto;
        background-color: #f5f7fa;
      }
      
      .newsletter-page {
        width: 210mm;
        height: 296mm;
        page-break-after: always;
        position: relative;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        background-color: #f5f7fa;
      }
      
      .newsletter-last-page {
        height: 297mm !important; /* Reduce height by 0mm to prevent empty second page */
        page-break-after: avoid !important; /* Prevent page break after the last page */
      }
      
      .newsletter-page-content {
        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: 0%;
        overflow: hidden;
      }
      
      @media print {
        .newsletter-page {
          margin: 0;
          box-shadow: none;
          page-break-after: always;
        }
        .newsletter-last-page {
          height: 297mm !important; /* Reduce height by 0mm to prevent empty second page */
          page-break-after: avoid !important; /* Prevent page break after the last page */
        }
        [data-ai="header"] {
          position: running(header);
        }
        [data-ai="footer"] {
          position: running(footer);
        }
        @page {
          size: A4 portrait;
          margin: 0;
          @top-center {
            content: element(header);
          }
          @bottom-center {
            content: element(footer);
          }
        }
      }
    `;
    pagesContainer.appendChild(styleElement);
    return pagesContainer.outerHTML;
  }
  
  const createFlexContainer = (articleElements: Element[]) => {
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.flexWrap = 'wrap';
    container.style.margin = '0 -10px';
    articleElements.forEach(article => {
      const articleClone = article.cloneNode(true) as HTMLElement;
      articleClone.style.flex = '1';
      articleClone.style.minWidth = '300px';
      articleClone.style.margin = '0 10px 20px';
      container.appendChild(articleClone);
    });
    return container;
  };
  
  const articlesPerPage = 2;
  const pagesContainer = document.createElement('div');
  pagesContainer.className = 'newsletter-pages-container';
  
  // Only create pages that will actually have content
  const actualTotalPages = Math.max(1, Math.ceil(articlesArray.length / articlesPerPage));
  
  for (let i = 0; i < actualTotalPages; i++) {
    const isFirstPage = i === 0;
    const isLastPage = i === actualTotalPages - 1;
    const pageContainer = document.createElement('div');
    pageContainer.className = 'newsletter-page';
    if (isLastPage) {
      pageContainer.classList.add('newsletter-last-page');
    }
    pageContainer.setAttribute('data-page', (i + 1).toString());
    const pageHeader = header.cloneNode(true) as HTMLElement;
    pageHeader.style.position = 'relative';
    pageContainer.appendChild(pageHeader);
    const pageMainContent = document.createElement('div');
    pageMainContent.setAttribute('data-ai', 'mainContent');
    pageMainContent.className = 'newsletter-page-content';
    if (mainContent instanceof HTMLElement) {
      copyStyles(mainContent, pageMainContent);
    }
    if (isFirstPage && introSection) {
      pageMainContent.appendChild(introSection.cloneNode(true));
    }
    const startIdx = i * articlesPerPage;
    const endIdx = Math.min(startIdx + articlesPerPage, articlesArray.length);
    const pageArticles = articlesArray.slice(startIdx, endIdx);
    const flexContainer = createFlexContainer(pageArticles);
    applyArticleLengthLimits(flexContainer, isFirstPage, newsletterType);
    pageMainContent.appendChild(flexContainer);
    pageContainer.appendChild(pageMainContent);
    if (isLastPage) {
      const pageFooter = footer ? footer.cloneNode(true) as HTMLElement : document.createElement('div');
      pageFooter.style.position = 'relative';
      pageContainer.appendChild(pageFooter);
    }
    pagesContainer.appendChild(pageContainer);
  }
  
  const styleElement = document.createElement('style');
  styleElement.textContent = `
    .newsletter-pages-container {
      display: flex;
      flex-direction: column;
      width: 210mm;
      margin: 0 auto;
      background-color: #f5f7fa;
    }
    
    .newsletter-page {
      width: 210mm;
      height: 296mm;
      page-break-after: always;
      position: relative;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
      background-color: #f5f7fa;
    }
    
    .newsletter-last-page {
      height: 297mm !important; /* Reduce height by 2mm to prevent empty second page */
      page-break-after: avoid !important; /* Prevent page break after the last page */
    }
    
    .newsletter-page-content {
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 0%;
      overflow: hidden;
    }
    
    @media print {
      .newsletter-page {
        margin: 0;
        box-shadow: none;
        page-break-after: always;
      }
      .newsletter-last-page {
        height: 297mm !important; /* Reduce height by 0mm to prevent empty second page */
        page-break-after: avoid !important; /* Prevent page break after the last page */
      }
      [data-ai="header"] {
        position: running(header);
      }
      [data-ai="footer"] {
        position: running(footer);
      }
      @page {
        size: A4 portrait;
        margin: 0;
        @top-center {
          content: element(header);
        }
        @bottom-center {
          content: element(footer);
        }
      }
    }
  `;
  pagesContainer.appendChild(styleElement);
  return pagesContainer.outerHTML;
};

export const fetchBlogPosts = async(ids: number[], jwt: string): Promise<BlogPost[]> => {
  try {
    const posts = await Promise.all(
      ids.map(async(id: number) => {
        const blogPostResponse = await fetch(`/api/blog-posts/${id}`, JWTHeader(jwt));
        if (!blogPostResponse.ok) {
          return null;
        }
        return blogPostResponse.json();
      }),
    );
    return posts.filter(Boolean);
  } catch {
    return [];
  }
};

/**
 * Handles the transaction manager presentation in the newsletter content
 * If includeTransactionManager is true and referent data exists, it replaces the content
 * Otherwise, it removes the element from the content
 * 
 * @param content The HTML content to process
 * @param company The company object containing branding settings
 * @param includeTransactionManager Whether to include the transaction manager presentation
 * @param referent Optional specific referent to use (for multi-referent PDF generation)
 * @returns The processed HTML content
 */
export const handleTransactionManagerPresentation = (
  content: string,
  company: Company | undefined,
  includeTransactionManager: boolean,
  referent?: ReferentMutation,
): string => {
  // If a specific referent is provided, use it; otherwise get the first valid referent
  let activeReferent: ReferentMutation | undefined = referent;
  
  if (!activeReferent && company?.brandingSettings && includeTransactionManager) {
    const referents = getValidReferents(company);
    activeReferent = referents[0];
  }
  
  if (activeReferent && includeTransactionManager) {
    const { title: referentTitle, firstname: referentFirstname, lastname: referentLastname } = activeReferent;
    
    if (referentTitle || referentFirstname || referentLastname || activeReferent.photoName) {
      // Create a temporary DOM element to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // Find all elements with data-ai="transactionManagerPresentation"
      const elements = tempDiv.querySelectorAll('[data-ai="transactionManagerPresentation"]');
      
      // Replace the content of each element while preserving attributes
      elements.forEach(element => {
        // Clear existing content
        element.innerHTML = '';
        
        // Add the new content with padding
        const contentDiv = document.createElement('div');
        contentDiv.style.padding = '15px';
        contentDiv.style.textAlign = 'justify';
        contentDiv.innerHTML = '';
        element.appendChild(contentDiv);
      });

      // Replace referent data placeholders throughout the document
      let processedContent = tempDiv.innerHTML;
      if (referentTitle) {
        processedContent = processedContent.replaceAll('[data-referent-title]', referentTitle);
      }
      if (referentFirstname) {
        processedContent = processedContent.replaceAll('[data-referent-firstname]', referentFirstname);
      }
      if (referentLastname) {
        processedContent = processedContent.replaceAll('[data-referent-lastname]', referentLastname);
      }
      const referentEmail = activeReferent.email || '';
      const referentPhone = activeReferent.phone ? formatPhoneNumber(activeReferent.phone) : '';
      processedContent = processedContent.replaceAll('[data-referent-email]', referentEmail);
      processedContent = processedContent.replaceAll('[data-referent-phone]', referentPhone);
      
      // Replace referent photo placeholder with actual photo URL if available
      if (activeReferent.photoName) {
        processedContent = processedContent.replaceAll('[data-referent-photo]', activeReferent.photoName);
      } else {
        processedContent = processedContent.replaceAll('[data-referent-photo]', '');
      }
      
      return processedContent;
    }
  }
  
  // Return content with any referent placeholders replaced with empty strings
  return content
    .replaceAll('[data-referent-title]', '')
    .replaceAll('[data-referent-firstname]', '')
    .replaceAll('[data-referent-lastname]', '')
    .replaceAll('[data-referent-photo]', '')
    .replaceAll('[data-referent-email]', '')
    .replaceAll('[data-referent-phone]', '');
};

/**
 * Process fixed newsletter content by applying company branding and transformations
 *
 * @param content The raw HTML content of the fixed newsletter
 * @param company The company object containing branding settings
 * @returns The processed HTML content with branding applied
 */
// Cache + in-flight dedupe : la liste des newsletters monte un MiniNewsletterPreview
// par ligne, et chacun rejouait le call privacy-policy pour le meme SIREN. On partage
// donc une seule promesse par SIREN pour eviter les appels en double.
const privacyPolicyLinkCache = new Map<string, Promise<string | null>>();

const fetchPrivacyPolicyLink = async(siren: string, jwt: string): Promise<string | null> => {
  try {
    console.info(`[NewsletterPreview] Fetching privacy policy link for siren: ${siren}`);
    const privacyLinkResponse = await fetch(`/api/public/dj/companies/${siren}/privacy-policy`, JWTHeader(jwt));

    if (!privacyLinkResponse.ok) {
      console.error(`[NewsletterPreview] Failed to fetch privacy policy link. Status: ${privacyLinkResponse.status}`);
      return null;
    }
    const data = await privacyLinkResponse.json();
    console.info('[NewsletterPreview] Privacy policy link response:', data);
    if (!data.link) {
      console.warn('[NewsletterPreview] No link found in privacy policy response');
      return null;
    }
    return `${DJ_URL}${data.link}`;
  } catch (error) {
    console.error('[NewsletterPreview] Error fetching privacy policy link:', error);
    return null;
  }
};

const getPrivacyPolicyLink = (siren: string, jwt: string): Promise<string | null> => {
  const cached = privacyPolicyLinkCache.get(siren);
  if (cached) {
    return cached;
  }
  const request = fetchPrivacyPolicyLink(siren, jwt).then((link) => {
    // On ne garde pas en cache les echecs pour autoriser un retry ulterieur.
    if (link === null) {
      privacyPolicyLinkCache.delete(siren);
    }
    return link;
  });
  privacyPolicyLinkCache.set(siren, request);
  return request;
};

const replacePrivacyPolicyLink = async(siren: string, jwt: string, replaceCallback: (link: string) => void) => {
  const fullLink = await getPrivacyPolicyLink(siren, jwt);
  if (fullLink) {
    console.info(`[NewsletterPreview] Replacing privacy policy link with: ${fullLink}`);
    replaceCallback(fullLink);
  }
};

/**
 * Process fixed newsletter content for a specific referent
 * This is used for multi-referent PDF generation where each referent gets their own PDF
 */
export const processFixedNewsletterContentForReferent = async(
  content: string,
  company: Company,
  referent: ReferentMutation,
  jwt?: string,
): Promise<string> => {
  try {
    const parser = new DOMParser();
    let processedContent = content;
    
    // Apply color replacements using the existing applyPreviewColors function
    if (company?.brandingSettings) {
      const brandingColors = {
        color1: company.brandingSettings.color1,
        color2: company.brandingSettings.color2,
        color3: company.brandingSettings.color3,
      };
      
      // Apply the color replacements
      processedContent = applyPreviewColors(processedContent, brandingColors);
      
      // Handle transaction manager presentation with specific referent
      processedContent = handleTransactionManagerPresentation(
        processedContent,
        company,
        true,
        referent, // Pass the specific referent
      );

      const emailSubcontractorName = company.brandingSettings.emailSubcontractorName || '';
      processedContent = processedContent.replaceAll('[data-sending-provider]', emailSubcontractorName);
    }
    
    // Add branding image if available
    if (company?.brandingSettings?.brandingImageName) {
      processedContent = processedContent.replaceAll(
        '[data-blog-logo]',
        company.brandingSettings.brandingImageName,
      );
    }
    
    // Add current month and year in French
    const date = new Date();
    const monthNames = ['JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'];
    const currentMonth = monthNames[date.getMonth()];
    const currentYear = date.getFullYear();
    
    // Replace date placeholders
    processedContent = processedContent.replaceAll('[data-current-month]', currentMonth);
    processedContent = processedContent.replaceAll('[data-current-year]', currentYear.toString());
    
    // Replace company info placeholders
    const tempDoc = parser.parseFromString(processedContent, 'text/html');
    const companyElements = tempDoc.querySelectorAll('[data-company-field]');
    companyElements.forEach(element => {
      const field = element.getAttribute('data-company-field');
      if (field) {
        // Use commercial name for "name" field if available
        if (field === 'name') {
          element.textContent = getCompanyDisplayName(company);
        } else if (company[field as keyof Company]) {
          element.textContent = String(company[field as keyof Company]);
        }
      }
    });
    
    // Add QR code if company siren is available
    // Use referent's external newsletter URL if available, otherwise use company blog
    if (company?.siren) {
      const externalUrl = referent.hasExternalNewsletter && referent.externalNewsletterUrl 
        ? referent.externalNewsletterUrl 
        : undefined;
      const qrCodeUrl = generateQRCodeUrl(company.siren, externalUrl);
      processedContent = tempDoc.body.innerHTML.replaceAll('[data-blog-qr]', qrCodeUrl);
      
      const unsubscribeUrl = buildUnsubscribeUrl(company);
      processedContent = processedContent.replaceAll('[data-unsubscribe-link]', unsubscribeUrl);

      if (jwt) {
        await replacePrivacyPolicyLink(company.siren, jwt, (link) => {
          processedContent = processedContent.replaceAll('[data-privacy-policy-link]', link);
        });
      }
    } else {
      processedContent = tempDoc.body.innerHTML.replaceAll('[data-blog-qr]', '');
      processedContent = processedContent.replaceAll('[data-unsubscribe-link]', '#');
    }
    
    // Parse the updated content
    const finalDoc = parser.parseFromString(processedContent, 'text/html');
    
    // Make sure all links open in a new tab
    const links = finalDoc.querySelectorAll('a');
    links.forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
    
    // Add newsletter-content class to main content if not present
    const mainContent = finalDoc.body.querySelector('main') || finalDoc.body.querySelector('.content') || finalDoc.body;
    if (!mainContent.classList.contains('newsletter-content')) {
      mainContent.classList.add('newsletter-content');
    }
    
    return finalDoc.body.innerHTML;
  } catch (error) {
    console.error('Error processing fixed newsletter content for referent:', error);
    return content;
  }
};

export const processFixedNewsletterContent = async(
  content: string,
  company: Company,
  jwt?: string,
): Promise<string> => {
  try {
    const parser = new DOMParser();
    let processedContent = content;
    
    // Apply color replacements using the existing applyPreviewColors function
    if (company?.brandingSettings) {
      const brandingColors = {
        color1: company.brandingSettings.color1,
        color2: company.brandingSettings.color2,
        color3: company.brandingSettings.color3,
      };
      
      // Apply the color replacements
      processedContent = applyPreviewColors(processedContent, brandingColors);
      
      // Handle transaction manager presentation and referent fields
      processedContent = handleTransactionManagerPresentation(
        processedContent,
        company,
        true, // Always include transaction manager for fixed newsletters if available
      );

      const emailSubcontractorName = company.brandingSettings.emailSubcontractorName || '';
      processedContent = processedContent.replaceAll('[data-sending-provider]', emailSubcontractorName);
    }
    
    // Add branding image if available
    if (company?.brandingSettings?.brandingImageName) {
      processedContent = processedContent.replaceAll(
        '[data-blog-logo]',
        company.brandingSettings.brandingImageName,
      );
    }
    
    // Add current month and year in French
    const date = new Date();
    const monthNames = ['JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'];
    const currentMonth = monthNames[date.getMonth()];
    const currentYear = date.getFullYear();
    
    // Replace date placeholders
    processedContent = processedContent.replaceAll('[data-current-month]', currentMonth);
    processedContent = processedContent.replaceAll('[data-current-year]', currentYear.toString());
    
    // Replace company info placeholders
    const tempDoc = parser.parseFromString(processedContent, 'text/html');
    const companyElements = tempDoc.querySelectorAll('[data-company-field]');
    companyElements.forEach(element => {
      const field = element.getAttribute('data-company-field');
      if (field) {
        // Use commercial name for "name" field if available
        if (field === 'name') {
          element.textContent = getCompanyDisplayName(company);
        } else if (company[field as keyof Company]) {
          element.textContent = String(company[field as keyof Company]);
        }
      }
    });
    
    // Add QR code if company siren is available
    // Use external newsletter URL from first referent if available
    if (company?.siren) {
      const firstReferent = company.brandingSettings?.referents?.[0];
      const externalUrl = firstReferent?.hasExternalNewsletter && firstReferent?.externalNewsletterUrl 
        ? firstReferent.externalNewsletterUrl 
        : undefined;
      const qrCodeUrl = generateQRCodeUrl(company.siren, externalUrl);
      processedContent = tempDoc.body.innerHTML.replaceAll('[data-blog-qr]', qrCodeUrl);
      
      const unsubscribeUrl = buildUnsubscribeUrl(company);
      processedContent = processedContent.replaceAll('[data-unsubscribe-link]', unsubscribeUrl);

      if (jwt) {
        console.info('[NewsletterPreview] JWT present, initiating privacy policy link replacement');
        await replacePrivacyPolicyLink(company.siren, jwt, (link) => {
          processedContent = processedContent.replaceAll('[data-privacy-policy-link]', link);
        });
      } else {
        console.warn('[NewsletterPreview] JWT missing, skipping privacy policy link replacement');
      }
    } else {
      processedContent = tempDoc.body.innerHTML.replaceAll('[data-blog-qr]', '');
      processedContent = processedContent.replaceAll('[data-unsubscribe-link]', '#');
    }
    
    // Parse the updated content
    const finalDoc = parser.parseFromString(processedContent, 'text/html');
    
    // Make sure all links open in a new tab
    const links = finalDoc.querySelectorAll('a');
    links.forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
    
    // Format for A4 if needed
    // Add newsletter-content class to main content if not present
    const mainContent = finalDoc.body.querySelector('main') || finalDoc.body.querySelector('.content') || finalDoc.body;
    if (!mainContent.classList.contains('newsletter-content')) {
      mainContent.classList.add('newsletter-content');
    }
    
    // Add necessary styling for fixed newsletters
    // This ensures fixed newsletters have the same styling as standard newsletters
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .newsletter-pages-container {
        display: flex;
        flex-direction: column;
        width: 210mm;
        margin: 0 auto;
        background-color: #f5f7fa;
      }
      
      .newsletter-page {
        width: 210mm;
        height: 297mm;
        page-break-after: always;
        position: relative;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        background-color: #f5f7fa;
      }
      
      .newsletter-last-page {
        height: 297mm !important; /* Reduce height by 0mm to prevent empty second page */
        page-break-after: avoid !important; /* Prevent page break after the last page */
      }
      
      .newsletter-page-content {
        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: 0%;
        overflow: hidden;
      }
      
      @media print {
        .newsletter-page {
          margin: 0;
          box-shadow: none;
          page-break-after: always;
        }
        .newsletter-last-page {
          height: 297mm !important; /* Reduce height by 0mm to prevent empty second page */
          page-break-after: avoid !important; /* Prevent page break after the last page */
        }
        [data-ai="header"] {
          position: running(header);
        }
        [data-ai="footer"] {
          position: running(footer);
        }
        @page {
          size: A4 portrait;
          margin: 0;
          @top-center {
            content: element(header);
          }
          @bottom-center {
            content: element(footer);
          }
        }
      }
    `;
    mainContent.appendChild(styleElement);
    
    return finalDoc.body.innerHTML;
  } catch (error) {
    console.error('Error processing fixed newsletter content:', error);
    return content; // Return original content if processing fails
  }
};

export const processNewsletterContent = async(
  content: string,
  company: Company,
  selectedArticles: number[],
  jwt: string,
  newsletterType: string = 'monthly',
  includeTransactionManager: boolean = false,
  template?: string,
  a4Formatted: boolean = true,
): Promise<string> => {
  try {
    const structuredContent: NewsletterStructuredContent = JSON.parse(content);
    let fetchedPosts: BlogPost[] = [];
    const templateName = template || company?.brandingSettings?.defaultTemplate;
    if (!templateName) {
      return JSON.stringify(structuredContent, null, 2);
    }
    const templateResponse = await fetch(`/api/newsletters-templates/${templateName}`, JWTHeader(jwt));
    if (!templateResponse.ok) {
      throw new Error('Failed to fetch template');
    }
    const templateData = await templateResponse.json();
    
    // Save the original template content before any modifications
    let originalTemplateContent = templateData.content;
    let processedContent = templateData.content;
    
    // Apply color replacements to both the processed content and original template content
    if (company?.brandingSettings) {
      const brandingColors = {
        color1: company.brandingSettings.color1,
        color2: company.brandingSettings.color2,
        color3: company.brandingSettings.color3,
      };

      // Handle transaction manager presentation and referent fields
      processedContent = handleTransactionManagerPresentation(
        processedContent,
        company,
        includeTransactionManager,
      );
      originalTemplateContent = handleTransactionManagerPresentation(
        originalTemplateContent,
        company,
        includeTransactionManager,
      );
      
      processedContent = applyPreviewColors(processedContent, brandingColors);
      originalTemplateContent = applyPreviewColors(originalTemplateContent, brandingColors);

      const emailSubcontractorName = company.brandingSettings.emailSubcontractorName || '';
      processedContent = processedContent.replaceAll('[data-sending-provider]', emailSubcontractorName);
      originalTemplateContent = originalTemplateContent.replaceAll('[data-sending-provider]', emailSubcontractorName);
    }
    
    if (company?.brandingSettings?.brandingImageName) {
      processedContent = processedContent.replaceAll(
        '[data-blog-logo]',
        company.brandingSettings.brandingImageName,
      );
      originalTemplateContent = originalTemplateContent.replaceAll(
        '[data-blog-logo]',
        company.brandingSettings.brandingImageName,
      );
    }
    
    // Add current month and year in French
    const date = new Date();
    const monthNames = ['JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'];
    const currentMonth = monthNames[date.getMonth()];
    const currentYear = date.getFullYear();
    
    processedContent = processedContent.replaceAll(
      '[data-current-month]',
      currentMonth,
    );
    processedContent = processedContent.replaceAll(
      '[data-current-year]',
      currentYear.toString(),
    );
    
    originalTemplateContent = originalTemplateContent.replaceAll(
      '[data-current-month]',
      currentMonth,
    );
    originalTemplateContent = originalTemplateContent.replaceAll(
      '[data-current-year]',
      currentYear.toString(),
    );
    
    if (company?.siren) {
      // Use external newsletter URL from first referent if available
      const firstReferent = company.brandingSettings?.referents?.[0];
      const externalUrl = firstReferent?.hasExternalNewsletter && firstReferent?.externalNewsletterUrl 
        ? firstReferent.externalNewsletterUrl 
        : undefined;
      const qrCodeUrl = generateQRCodeUrl(company.siren, externalUrl);
      processedContent = processedContent.replaceAll(
        '[data-blog-qr]',
        qrCodeUrl,
      );
      originalTemplateContent = originalTemplateContent.replaceAll(
        '[data-blog-qr]',
        qrCodeUrl,
      );

      const unsubscribeUrl = buildUnsubscribeUrl(company);
      processedContent = processedContent.replaceAll(
        '[data-unsubscribe-link]',
        unsubscribeUrl,
      );
      originalTemplateContent = originalTemplateContent.replaceAll(
        '[data-unsubscribe-link]',
        unsubscribeUrl,
      );
    }
    
    
  try {
    // Fetch privacy policy link
    if (company.siren) {
      await replacePrivacyPolicyLink(company.siren, jwt, (link) => {
        processedContent = processedContent.replaceAll('[data-privacy-policy-link]', link);
        originalTemplateContent = originalTemplateContent.replaceAll('[data-privacy-policy-link]', link);
      });
    }
  } catch (error) {
    console.error('Error processing privacy policy link:', error);
  }

  if (selectedArticles?.length > 0) {
      const posts = await fetchBlogPosts(selectedArticles, jwt);
      fetchedPosts = posts;
      processedContent = await processSelectedBlogPosts(
        company.siren,
        posts,
        processedContent,
        processedContent,
        templateData.blogTemplate || '',
        templateData.blogTemplateGroup || '',
        newsletterType,
      );
    }
    let finalContent = processPreviewContent(processedContent, structuredContent);

    if (a4Formatted) {
      finalContent = formatForA4Pages(finalContent, fetchedPosts, newsletterType, company.siren, originalTemplateContent);
    }
    return finalContent;
  } catch {
    return content;
  }
};

export const revertToColorPlaceholders = (content: string, previewColors: PreviewColors): string => {
  if (!content) { return ''; }
  return content
    .replaceAll(previewColors.color1, '[data-blog-color1]')
    .replaceAll(previewColors.color2, '[data-blog-color2]')
    .replaceAll(previewColors.color3, '[data-blog-color3]');
};

export const extractBlogTemplates = (content: string): {
  blogTemplate: string | null;
  blogTemplateGroup: string | null;
} => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const blogSection = doc.querySelector('[data-blog-posts]');
  const blogGroup = doc.querySelector('[data-blog-posts-group]');
  return {
    blogTemplate: blogSection?.outerHTML || null,
    blogTemplateGroup: blogGroup?.outerHTML || null,
  };
}; 